import { useMemo, useState } from "react";
import { GridColDef, GridRowSelectionModel } from "@mui/x-data-grid";
import { IconButton, Tooltip } from "@mui/material";
import { MdEdit } from "react-icons/md";

import CustomGrid from "components/lists/CustomGrid";
import EditOrderArticleModal from "./EditOrderArticleModal";
import { formatDateOnlyDMY } from "helpers";
import { buildObservationPreviewSummary } from "helpers/movementArticleObservations";
import { getOrderClientFieldConfig } from "helpers/orderClientFields";
import { Article, Client, OrderDraftArticleRow } from "types";

type Props = {
  rows: OrderDraftArticleRow[];
  articles: Article[];
  client: Client | null;
  disabled?: boolean;
  onEditRow: (row: OrderDraftArticleRow) => void;
};

export default function DispatchOrderArticlesList({
  rows,
  articles,
  client,
  disabled = false,
  onEditRow,
}: Props) {
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [editingRow, setEditingRow] = useState<OrderDraftArticleRow | null>(null);
  const clientFieldConfig = useMemo(
    () => getOrderClientFieldConfig(client),
    [client],
  );

  const rowsWithArticleData = useMemo(
    () =>
      rows.map((row) => {
        const article = articles.find(
          (currentArticle) => currentArticle.id === row.article_id,
        );

        return {
          ...row,
          id: row.localId,
          code: article?.code ?? "",
          description: article?.description ?? "",
          observation:
            buildObservationPreviewSummary(row.observation_preview_units ?? []) ??
            "-",
          subtotal_units:
            (Number(row.amount) || 0) * (Number(row.amount_per_container) || 0),
          subtotal_weight:
            (Number(row.amount) || 0) * Number(article?.weight ?? 0),
        };
      }),
    [articles, rows],
  );

  const columns = useMemo(() => {
    const fields: Array<GridColDef & { visible?: boolean }> = [
      {
        field: "code",
        headerName: "Codigo",
        headerAlign: "center",
        flex: 1,
      },
      {
        field: "description",
        headerName: "Descripcion",
        headerAlign: "center",
        flex: 1.5,
      },
      {
        field: "amount",
        headerName: "Cantidad",
        headerAlign: "center",
        align: "center",
        flex: 0.8,
      },
      {
        field: "amount_per_container",
        headerName: "Cant. x Cont.",
        headerAlign: "center",
        align: "center",
        flex: 0.9,
      },
      {
        field: "subtotal_units",
        headerName: "Subtotal",
        headerAlign: "center",
        align: "center",
        flex: 0.8,
      },
      {
        field: "subtotal_weight",
        headerName: "Peso",
        headerAlign: "center",
        align: "center",
        flex: 0.8,
        visible: clientFieldConfig.detail.showPeso,
      },
      {
        field: "location_code",
        headerName: "Ubicación",
        headerAlign: "center",
        align: "center",
        flex: 0.9,
      },
      {
        field: "lote",
        headerName: "Lote",
        headerAlign: "center",
        align: "center",
        flex: 0.9,
        visible: clientFieldConfig.detail.showLote,
      },
      {
        field: "oc",
        headerName: "OC",
        headerAlign: "center",
        align: "center",
        flex: 0.9,
        visible: clientFieldConfig.detail.showOc,
      },
      {
        field: "fecha_vencimiento",
        headerName: "Fecha de Vencimiento",
        headerAlign: "center",
        align: "center",
        flex: 1,
        visible: clientFieldConfig.detail.showFechaVencimiento,
        valueGetter: (params) =>
          formatDateOnlyDMY(params.row.fecha_vencimiento) || "-",
      },
      {
        field: "observation",
        headerName: "Observaciones",
        headerAlign: "center",
        flex: 1.2,
      },
      {
        field: "actions",
        headerName: "Acciones",
        headerAlign: "center",
        align: "center",
        sortable: false,
        filterable: false,
        minWidth: 90,
        renderCell: (params) => (
          <Tooltip title="Editar artículo">
            <span>
              <IconButton
                size="small"
                disabled={disabled}
                onClick={() => {
                  setEditingRow(
                    rows.find((row) => row.localId === Number(params.row.id)) ?? null,
                  );
                }}
              >
                <MdEdit />
              </IconButton>
            </span>
          </Tooltip>
        ),
      },
    ];

    return fields.filter((field) => field.visible !== false);
  }, [clientFieldConfig.detail, disabled, rows]);

  return (
    <>
      <CustomGrid<any>
        structure={{
          name: "dispatch_order_articles",
          labelName: "Artículos del despacho",
          fields: columns,
        }}
        rows={rowsWithArticleData}
        selectionModel={selectionModel}
        onRowSelection={setSelectionModel}
        checkboxSelection={false}
        getRowId={(row) => row.id}
      />

      {editingRow && (
        <EditOrderArticleModal
          open
          handleOpen={() => setEditingRow(null)}
          value={editingRow}
          articles={articles}
          client={client}
          readOnlyIdentityFields
          onEdit={(row) => {
            onEditRow(row);
            setEditingRow(null);
          }}
        />
      )}
    </>
  );
}
